// Copyright (c) 2025 WiseTime. All rights reserved.

import { EMPTY, Observable, of, interval } from "rxjs"
import { Action } from "redux"
import { filter, withLatestFrom, mergeMap } from "rxjs/operators"
import * as Slice from "../slices/timeline"
import * as Actions from "../actions"
import { RootState } from "../../../app/store"

// 3 hour TTL for timesheet field configs
const TIMESHEET_FIELD_CONFIG_TTL_MS = 3 * 60 * 60 * 1000

/**
 * Checks if a timesheet field config has expired based on its timestamp
 * @param configHash - The hash identifier for the config
 * @param timestamps - Cache of config timestamps
 * @returns true if the config is expired or missing, false otherwise
 */
export const isTimesheetFieldConfigExpired = (
  configHash: string,
  timestamps: Slice.TimesheetFieldConfigsCacheTimestamps,
): boolean => {
  const timestamp = timestamps[configHash]
  if (!timestamp) {
    return true
  }
  return Date.now() - timestamp > TIMESHEET_FIELD_CONFIG_TTL_MS
}

export const periodicRefreshTimesheetFieldConfigs = (
  intervalMs = TIMESHEET_FIELD_CONFIG_TTL_MS,
) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.periodicRefreshTimesheetFieldConfigs.match),
      mergeMap(() => 
        interval(intervalMs).pipe(
          withLatestFrom(state$),
          mergeMap(([_, state]) => {
            const timesheetFieldConfigsCache = Slice.selectTimesheetFieldConfigsCache(state)
            const timesheetFieldConfigsCacheTimestamps = Slice.selectTimesheetFieldConfigsCacheTimestamps(state)

            // Check if any configs are expired
            const hasExpiredConfigs = Object.keys(timesheetFieldConfigsCache)
              .some(configHash => isTimesheetFieldConfigExpired(configHash, timesheetFieldConfigsCacheTimestamps))

            if (!hasExpiredConfigs) {
              return EMPTY
            }

            return of(Actions.refreshTimesheetFieldConfigs())
          })
        )
      )
    )

export default periodicRefreshTimesheetFieldConfigs()
